/* Prest custom JS on Dawn: review carousels, flavor tabs, bundle builder.
   Cart adds go through Dawn's cart drawer (cart-drawer custom element). */

(function () {
  'use strict';

  const money = (cents) => '$' + (cents / 100).toFixed(2);

  /* ---------------- Dawn cart integration ---------------- */

  const FRIENDLY_ADD_ERROR = 'Sorry — we couldn’t add that to your cart. Please try again.';

  function friendlyError(message) {
    const err = new Error(message);
    err.prestFriendly = true;
    return err;
  }

  async function addItemsToCart(items) {
    const cart =
      document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const body = { items };

    try {
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      }
    } catch (uiError) {
      /* Section prep failed (browser quirk) — add without drawer refresh data. */
      console.warn('[Prest] cart section prep failed:', uiError);
      delete body.sections;
      delete body.sections_url;
    }

    const res = await fetch(window.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const msg = typeof err.description === 'string' && err.description ? err.description : null;
      throw friendlyError(msg || (window.cartStrings && window.cartStrings.error) || FRIENDLY_ADD_ERROR);
    }

    /* From here on the items ARE in the cart — nothing below is allowed to
       surface an error to the shopper (Safari has thrown internal DOM errors
       during the drawer refresh; see support screenshots). */
    let state = null;
    try {
      state = await res.json();
    } catch (parseError) {
      console.warn('[Prest] cart add succeeded but response parse failed:', parseError);
    }

    try {
      if (cart && state && state.sections && typeof cart.renderContents === 'function') {
        cart.renderContents(state);
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
      } else {
        window.location.href = window.routes.cart_url;
      }
    } catch (uiError) {
      console.warn('[Prest] cart add succeeded but drawer refresh failed:', uiError);
      /* Show the shopper their cart instead of an error for a successful add. */
      window.location.href = window.routes.cart_url;
    }
    return state;
  }

  window.PrestCart = { addItems: addItemsToCart };

  /* ---------------- AJAX add for simple card forms ---------------- */

  function initAjaxForms() {
    document.querySelectorAll('form[data-ajax-cart]').forEach((form) => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('[type="submit"]');
        const original = btn.textContent;
        btn.textContent = 'Adding…';
        btn.disabled = true;

        const fd = new FormData(form);
        const item = {
          id: parseInt(fd.get('id'), 10),
          quantity: parseInt(fd.get('quantity') || '1', 10),
        };
        const plan = fd.get('selling_plan');
        if (plan) item.selling_plan = parseInt(plan, 10);

        try {
          await addItemsToCart([item]);
        } catch (err) {
          alert(err.message);
        } finally {
          btn.textContent = original;
          btn.disabled = false;
        }
      });
    });
  }

  /* ---------------- Strip filler paragraphs from tab copy ----------------
     Shopify's rich-text editor pads descriptions with <p>&nbsp;</p> lines;
     CSS :empty can't match them, so remove them here. */

  function initCleanTabCopy() {
    document.querySelectorAll('.flavor-tabs__panel p').forEach((p) => {
      if (!p.textContent.replace(/ /g, ' ').trim() && !p.querySelector('img, iframe, video')) {
        p.remove();
      }
    });
  }

  /* ---------------- Product gallery ---------------- */

  function initGalleries() {
    document.querySelectorAll('[data-gallery]').forEach((gallery) => {
      const main = gallery.querySelector('.product-gallery__main img');
      if (!main) return;
      gallery.querySelectorAll('.product-gallery__thumb').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          main.src = thumb.dataset.src;
          main.srcset = '';
          gallery.querySelectorAll('.product-gallery__thumb').forEach((t) => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        });
      });
    });
  }

  /* ---------------- Quantity steppers ---------------- */

  function initSteppers() {
    document.querySelectorAll('.qty-stepper[data-stepper]').forEach((stepper) => {
      const input = stepper.querySelector('input');
      stepper.querySelectorAll('button').forEach((btn) => {
        btn.addEventListener('click', () => {
          const delta = parseInt(btn.dataset.delta, 10);
          input.value = Math.max(1, parseInt(input.value || '1', 10) + delta);
          input.dispatchEvent(new Event('change'));
        });
      });
    });
  }

  /* ---------------- Transparent header over hero (Pistakio style) ---------------- */

  function initTransparentHeader() {
    const hero = document.querySelector('.hero-video');
    const headerWrapper = document.querySelector('.header-wrapper');
    if (!hero || !headerWrapper) return;
    const headerSection = headerWrapper.closest('.shopify-section');
    const heroSection = hero.closest('.shopify-section') || hero;

    headerSection.classList.add('prest-glass');

    const pullUp = () => {
      const h = headerWrapper.offsetHeight;
      heroSection.style.marginTop = `-${h}px`;
      hero.style.paddingTop = `${h}px`;
    };
    pullUp();
    window.addEventListener('resize', pullUp);

    /* Cream wordmark over the video, green wordmark on the solid header */
    const logo = headerWrapper.querySelector('.header__heading-logo');
    const CREAM_LOGO = 'https://cdn.shopify.com/s/files/1/0708/6777/9677/files/Prest_Logo_Cream.png';
    let greenLogo = null;
    if (logo) {
      greenLogo = { src: logo.src, srcset: logo.srcset };
      new Image().src = CREAM_LOGO; // preload so the swap never flashes
    }

    const sync = () => {
      const solid = window.scrollY > 24;
      headerSection.classList.toggle('prest-solid', solid);
      if (logo) {
        if (solid && logo.src !== greenLogo.src) {
          logo.src = greenLogo.src;
          logo.srcset = greenLogo.srcset;
        } else if (!solid && !logo.src.includes('Prest_Logo_Cream')) {
          logo.srcset = '';
          logo.src = CREAM_LOGO;
        }
      }
    };
    window.addEventListener('scroll', sync, { passive: true });
    sync();
  }

  /* ---------------- Success popups (newsletter / contact) ---------------- */

  function initPopups() {
    document.addEventListener('click', (e) => {
      const closer = e.target.closest('[data-popup-close]');
      if (closer) closer.closest('[data-prest-popup]')?.remove();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') document.querySelector('[data-prest-popup]')?.remove();
    });
  }

  /* ---------------- Review carousel arrows ---------------- */

  function initCarousels() {
    document.querySelectorAll('[data-carousel]').forEach((section) => {
      const track = section.querySelector('.reviews-carousel__track');
      if (!track) return;
      section.querySelectorAll('.carousel-arrow').forEach((arrow) => {
        arrow.addEventListener('click', () => {
          const dir = parseInt(arrow.dataset.dir, 10);
          track.scrollBy({ left: dir * (track.firstElementChild.offsetWidth + 16), behavior: 'smooth' });
        });
      });
    });
  }

  /* ---------------- Flavor detail tabs ---------------- */

  function initFlavorTabs() {
    document.querySelectorAll('[data-flavor-tabs]').forEach((root) => {
      const tabs = [...root.querySelectorAll('.flavor-tabs__tab')];
      const panels = [...root.querySelectorAll('.flavor-tabs__panel')];
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => {
          tabs.forEach((t) => t.classList.remove('is-active'));
          panels.forEach((p) => (p.hidden = true));
          tab.classList.add('is-active');
          panels[i].hidden = false;
        });
      });
    });
  }

  /* ---------------- Bundle builder (shared by product + bundle pages) ---------------- */

  function initBuilders() {
    document.querySelectorAll('[data-builder]').forEach((root) => {
      const flavors = [...root.querySelectorAll('[data-flavor]')];
      const stops = [...root.querySelectorAll('.tier-slider__stop')];
      const fill = root.querySelector('.tier-slider__fill');
      const puck = root.querySelector('.tier-slider__puck');
      const addBtn = root.querySelector('[data-bundle-add]');
      const totalEl = root.querySelector('[data-total]');
      const compareEl = root.querySelector('[data-compare]');
      const progressLabel = root.querySelector('[data-progress-label]');
      const progressFill = root.querySelector('[data-progress-fill]');
      const modeWrap = root.querySelector('[data-bundle-mode]');
      const planSelect = root.querySelector('[data-bundle-plan]');
      const shipFlag = root.querySelector('.tier-slider__ship');
      const emptyCta = root.dataset.emptyCta || 'Add a pouch to start';
      const addCta = root.dataset.addCta || 'Add to cart';
      const freeShipQty = parseInt(root.dataset.freeShipQty || '0', 10);
      const subscribePercent = parseFloat(root.dataset.subscribePercent || '0');
      const sliderMax = Math.max(...stops.map((s) => parseInt(s.dataset.qty, 10)), 1);

      const counts = new Map(flavors.map((f) => [f, parseInt(f.dataset.preselect || '0', 10)]));

      /* populate frequency dropdown from the first flavor that has selling plans */
      const planned = flavors.find((f) => f.dataset.sellingPlans);
      let plans = [];
      if (planned && planSelect) {
        plans = JSON.parse(planned.dataset.sellingPlans);
        planSelect.innerHTML = plans.map((p) => `<option value="${p.id}">${p.name}</option>`).join('');
        planSelect.hidden = plans.length < 2;
      }
      if (modeWrap && !plans.length) modeWrap.hidden = true;

      const tierFor = (qty) => {
        let best = null;
        stops.forEach((t) => {
          const need = parseInt(t.dataset.qty, 10);
          if (qty >= need && (!best || need > parseInt(best.dataset.qty, 10))) best = t;
        });
        return best;
      };

      const nextTierAfter = (qty) => {
        let next = null;
        stops.forEach((t) => {
          const need = parseInt(t.dataset.qty, 10);
          if (need > qty && (!next || need < parseInt(next.dataset.qty, 10))) next = t;
        });
        return next;
      };

      const isSubscribe = () =>
        modeWrap && !modeWrap.hidden && modeWrap.querySelector('input[value="subscribe"]')?.checked;

      function update() {
        let qty = 0;
        let subtotal = 0;
        counts.forEach((count, f) => {
          qty += count;
          subtotal += count * parseInt(f.dataset.price, 10);
          const stepper = f.querySelector('[data-flavor-stepper]');
          const addOne = f.querySelector('[data-add]');
          if (stepper) stepper.hidden = count === 0;
          if (addOne) addOne.hidden = count > 0;
          const countEl = f.querySelector('[data-count]');
          if (countEl) countEl.textContent = count;
          f.classList.toggle('has-items', count > 0);
        });

        const active = tierFor(qty);
        stops.forEach((t) => t.classList.toggle('is-reached', qty >= parseInt(t.dataset.qty, 10)));

        /* Free-shipping flag wiggles the moment the threshold is reached */
        if (shipFlag && freeShipQty > 0) {
          const reached = qty >= freeShipQty;
          if (reached && !shipFlag.classList.contains('is-reached')) {
            shipFlag.classList.remove('is-reached');
            void shipFlag.offsetWidth; /* restart the animation */
            shipFlag.classList.add('is-reached');
          } else if (!reached) {
            shipFlag.classList.remove('is-reached');
          }
        }

        const percent = active ? parseFloat(active.dataset.percent) : 0;
        let discounted = subtotal * (1 - percent / 100);
        if (isSubscribe() && subscribePercent > 0) {
          discounted = discounted * (1 - subscribePercent / 100);
        }
        discounted = Math.round(discounted);

        if (fill) fill.style.width = Math.min(100, (qty / sliderMax) * 100) + '%';
        if (puck) puck.style.left = Math.min(100, (qty / sliderMax) * 100) + '%';

        totalEl.textContent = money(discounted);
        if (discounted < subtotal && subtotal > 0) {
          compareEl.hidden = false;
          compareEl.textContent = money(subtotal);
        } else {
          compareEl.hidden = true;
        }

        const next = nextTierAfter(qty);
        if (qty === 0) {
          progressLabel.textContent = emptyCta;
          progressFill.style.width = '0%';
        } else if (freeShipQty > 0 && qty < freeShipQty && (!next || freeShipQty <= parseInt(next.dataset.qty, 10))) {
          const parts = [];
          if (next) parts.push('Add ' + (parseInt(next.dataset.qty, 10) - qty) + ' more for ' + next.dataset.percent + '% off');
          parts.push('free shipping at ' + freeShipQty + '+');
          progressLabel.textContent = parts.join(' · ');
          progressFill.style.width = Math.min(100, (qty / (next ? parseInt(next.dataset.qty, 10) : freeShipQty)) * 100) + '%';
        } else if (next) {
          progressLabel.textContent = 'Add ' + (parseInt(next.dataset.qty, 10) - qty) + ' more to unlock ' + next.dataset.percent + '% off';
          progressFill.style.width = Math.min(100, (qty / parseInt(next.dataset.qty, 10)) * 100) + '%';
        } else {
          let label = percent > 0 ? 'Max savings unlocked — ' + percent + '% off!' : 'Ready to go!';
          if (freeShipQty > 0 && qty >= freeShipQty) label += ' + FREE shipping';
          progressLabel.textContent = label;
          progressFill.style.width = '100%';
        }

        addBtn.disabled = qty === 0;
        addBtn.textContent = qty === 0 ? emptyCta : addCta + ' · ' + qty + ' pouch' + (qty === 1 ? '' : 'es');
      }

      flavors.forEach((f) => {
        const add = f.querySelector('[data-add]');
        const plus = f.querySelector('[data-plus]');
        const minus = f.querySelector('[data-minus]');
        if (add) add.addEventListener('click', () => { counts.set(f, 1); update(); });
        if (plus) plus.addEventListener('click', () => { counts.set(f, counts.get(f) + 1); update(); });
        if (minus) minus.addEventListener('click', () => { counts.set(f, Math.max(0, counts.get(f) - 1)); update(); });
      });

      stops.forEach((t) => {
        t.addEventListener('click', () => {
          const target = parseInt(t.dataset.qty, 10);
          let current = 0;
          counts.forEach((c) => (current += c));
          if (current >= target) return;
          let deficit = target - current;
          const available = flavors.filter((f) => f.dataset.available !== 'false');
          let i = 0;
          while (deficit > 0 && available.length) {
            const f = available[i % available.length];
            counts.set(f, counts.get(f) + 1);
            deficit--;
            i++;
          }
          update();
        });
      });

      if (modeWrap) {
        modeWrap.querySelectorAll('input[name^="bundle_mode"]').forEach((r) => {
          r.addEventListener('change', () => {
            modeWrap.querySelectorAll('.purchase-option').forEach((o) => {
              o.classList.toggle('is-selected', o.querySelector('input').checked);
            });
            update();
          });
        });
      }

      addBtn.addEventListener('click', async () => {
        const planId = isSubscribe() && planSelect && planSelect.value ? parseInt(planSelect.value, 10) : null;
        const items = [];
        counts.forEach((count, f) => {
          if (count > 0) {
            const item = { id: parseInt(f.dataset.variantId, 10), quantity: count };
            if (planId) item.selling_plan = planId;
            items.push(item);
          }
        });
        if (!items.length) return;

        addBtn.disabled = true;
        const original = addBtn.textContent;
        addBtn.textContent = 'Adding…';
        try {
          await addItemsToCart(items);
          counts.forEach((_, f) => counts.set(f, 0));
        } catch (err) {
          alert(err.message);
        } finally {
          addBtn.textContent = original;
          addBtn.disabled = false;
          update();
        }
      });

      update();
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initTransparentHeader();
    initPopups();
    initAjaxForms();
    initCleanTabCopy();
    initGalleries();
    initSteppers();
    initCarousels();
    initFlavorTabs();
    initBuilders();
  });
})();
